import React, { useState, useRef, useEffect } from "react";
import { Button } from "../../../globalStyles";

// ✅ Format minutes into "X hr Y mins"
const formatDuration = (mins) => {
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;

  if (hours && minutes) return `${hours} hr ${minutes} mins`;
  if (hours) return `${hours} hr`;
  return `${minutes} mins`;
};

const MultiSelectModal = ({
  name,
  label,
  options,
  selectedValues,
  setSelectedValues,
  selectedId,
  setSelectedId,
  totalTime,
  setTotalTime,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(options[0]?.category);
  const categoryRefs = useRef({});
  const tabListRef = useRef(null);

  const groupedOptions = options.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  const toggleSelection = (item) => {
    setSelectedValues((prev) => {
      const exists = prev.find((v) => v._id === item._id);
      if (exists) {
        return prev.filter((v) => v._id !== item._id);
      } else {
        return [...prev, { _id: item._id, title: item.title, desc: item.desc }];
      }
    });

    setActiveCategory(item.category);
  };

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
    categoryRefs.current[category]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveCategory(entry.target.getAttribute("data-category"));
          }
        });
      },
      { threshold: 0.6 }
    );

    Object.keys(groupedOptions).forEach((category) => {
      if (categoryRefs.current[category]) {
        observer.observe(categoryRefs.current[category]);
      }
    });

    return () => observer.disconnect();
  }, [groupedOptions]);

  useEffect(() => {
    if (setSelectedId) {
      const ids = selectedValues.map((v) => v._id);
      setSelectedId(ids);
    }
  }, [selectedValues, setSelectedId]);

  useEffect(() => {
    if (!setTotalTime) return;

    const total = selectedValues.reduce((acc, item) => {
      const desc = item.desc?.toLowerCase() || "";

      const hourMatch = desc.match(/(\d+)\s*hr/);
      const minMatch = desc.match(/(\d+)\s*min/);

      const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
      const mins = minMatch ? parseInt(minMatch[1], 10) : 0;

      return acc + hours * 60 + mins;
    }, 0);

    setTotalTime(total);
  }, [selectedValues, setTotalTime]);

  return (
    <div className="space-y-2  z-[9999]">
      <h2 className="text-2xl font-bold mt-8">{label}</h2>

      <button
        onClick={() => setIsOpen(true)}
        className={`w-full p-3 rounded-lg border text-left min-h-[2.5rem] ${
          selectedValues.length > 0
            ? "border-yellowlight text-white"
            : "border-border text-gray-400"
        }`}
      >
        {selectedValues.length === 0 ? (
          <p className="text-textGray text-sm italic">
            Click box to select services
          </p>
        ) : (
          <>
            {selectedValues.map((selected, i) => {
              const item = options.find((o) => o._id === selected._id);
              return (
                <div key={i}>
                  <div className="font-medium text-text flex lg:items-center sm:items-start">
                    <div className="w-6 h-6 bg-yellowlight rounded-md flex items-center justify-center m-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 text-yellowdark"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <p className="ml-2 text-xl">
                      {item?.title}
                      {item?.desc && (
                        <span className="text-md text-gray-500">
                          {" "}
                          ({item.desc})
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              );
            })}

            <p className="text-yellowlight font-medium text-right mt-2">
              Total Duration: {formatDuration(totalTime)}
            </p>
          </>
        )}
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 z-[999] flex items-center justify-center">
          <div
            className="bg-dry border border-yellowlight w-full max-w-2xl max-h-[95vh] rounded-xl p-4 overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-yellowlight mb-3">{label}</h2>

            <div
              ref={tabListRef}
              className="flex overflow-x-auto no-scrollbar space-x-2 py-2 mb-4"
            >
              {Object.keys(groupedOptions).map((category) => (
                <button
                  key={category}
                  data-category={category}
                  onClick={() => handleCategoryClick(category)}
                  className={`px-4 py-2 rounded-full text-xl font-medium whitespace-nowrap ${
                    category === activeCategory
                      ? "bg-yellowlight text-yellowdark"
                      : "bg-none text-text"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="overflow-y-auto max-h-[50vh] pr-1">
              {Object.keys(groupedOptions).map((category) => (
                <div
                  key={category}
                  data-category={category}
                  ref={(el) => (categoryRefs.current[category] = el)}
                  className="mb-6"
                >
                  <h3 className="text-2xl font-semibold mb-2 text-text">
                    {category}
                  </h3>

                  {groupedOptions[category].map((item) => {
                    const isSelected = selectedValues.some(
                      (v) => v._id === item._id
                    );
                    return (
                      <div
                        key={item._id}
                        onClick={() => toggleSelection(item)}
                        className={`flex justify-between items-start p-4 rounded-xl border ${
                          isSelected
                            ? "border-yellowlight ring-2 ring-yellowlight"
                            : "border-border"
                        } hover:border-yellowlight cursor-pointer transition mb-4 bg-drylight`}
                      >
                        <div className="flex flex-col gap-1">
                          <div className="font-normal text-text">
                            <p className="text-2xl">
                              {item.title}
                              {item.desc && (
                                <span className="text-xl text-textGray">
                                  {" "}
                                  ({item.desc})
                                </span>
                              )}
                            </p>
                          </div>

                          <div className="text-2xl text-text">
                            {item.description}
                          </div>
                          <div className="text-xl font-medium text-textGray mt-1">
                            from ${item.price}
                          </div>
                        </div>
                        <div className="mt-1">
                          {isSelected ? (
                            <div className="w-6 h-6 bg-yellowlight rounded-md flex items-center justify-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4 text-yellowdark"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-6 h-6 border border-gray-300 rounded-md" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <Button
                className="text-sm px-4 py-2 bg-none text-yellowlight hover:text-white rounded-md"
                onClick={() => setSelectedValues([])}
              >
                Clear
              </Button>
              <Button
                className="text-sm px-4 py-2 bg-yellowlight text-yellowdark rounded-md"
                onClick={() => setIsOpen(false)}
              >
                Done
              </Button>
            </div>

            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-black"
              onClick={() => setIsOpen(false)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectModal;
